"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
exports.__esModule = true;
var react_1 = require("react");
var ts_invariant_1 = __importDefault(require("ts-invariant"));
/**
 * The callback calls when a new value is suggested.
 *
 * @callback onChangingCallback
 * @param {object} value - The suggested value
 */
/**
 * The callback calls when value has changed for a controlled component.
 *
 * @callback onChangeCallback
 * @param {object} value - The new value
 */
/**
 * Store value for a controllable or uncontrollable component.
 * For a controllable component, the value is handled by external logic like the component, redux, ... .
 * For an uncontrollable component, the value is handled by the hook itself.
 *
 * @param {object} props.value - The current value for an controllable hook or the initial value for a uncontrollable hook
 * @param {onChangingCallback} props.onChanging - The callback where you can update the value for an controllable hook
 * @param {onChangeCallback} props.onChange - The callback when the value has changed
 * @returns {array} - An array that contains the current value and a function to change the current value
 */
var useControllableValue = function (_a) {
    var value = _a.value, onChange = _a.onChange, onChanging = _a.onChanging;
    var isControlled = react_1.useRef(!!onChanging).current;
    var initialValue = react_1.useRef(value).current;
    var _b = react_1.useState(value), stateValue = _b[0], setStateValue = _b[1];
    var currentValue = isControlled ? value : stateValue;
    // The onChanging prop cannot be changed after being initialized.
    react_1.useEffect(function () {
        ts_invariant_1["default"](isControlled === !!onChanging, 'useControllableValue - Elements should not switch from uncontrolled to controlled (or vice versa).');
    }, [onChanging, isControlled]);
    // The value prop can't be dynamic for an uncontrolled component.
    react_1.useEffect(function () {
        ts_invariant_1["default"](isControlled || initialValue === value, "useControllableValue - An uncontrolled component can't changed the value after being initialized.");
    }, 
    // We use JSON.stringify for value for a deep equals comparaison
    [JSON.stringify(value), initialValue, isControlled]);
    // Calls onChange when currentValue has changed
    react_1.useEffect(function () {
        if (onChange) {
            onChange(currentValue);
        }
    }, [currentValue, onChange]);
    var changeValue = react_1.useCallback(function (val) {
        /* istanbul ignore else */
        if (!isControlled) {
            setStateValue(val);
        }
        else if (onChanging) {
            onChanging(val);
        }
    }, [isControlled, onChanging]);
    return [currentValue, changeValue];
};
exports["default"] = useControllableValue;
//# sourceMappingURL=useControllableValue.js.map